import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { listingsApi, toAbs } from '../api/client';
import { ArrowLeft, MapPin, Star, Heart, Phone, MessageCircle, Shield,
         Loader2, Calendar, Globe, ChevronLeft, ChevronRight, Grid3x3, Play } from 'lucide-react';
import MessageModal from '../components/MessageModal';
import ListingCard from '../components/ListingCard';
import { useFavorites } from '../context/FavoritesContext';

const WhatsappIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M20.52 3.48A11.86 11.86 0 0 0 12.07 0C5.46 0 .08 5.37.08 11.98c0 2.11.55 4.17 1.6 5.98L0 24l6.21-1.63a11.95 11.95 0 0 0 5.86 1.5h.01c6.6 0 11.98-5.37 11.98-11.98 0-3.2-1.25-6.21-3.54-8.41ZM12.08 21.8h-.01a9.9 9.9 0 0 1-5.04-1.38l-.36-.21-3.68.97.98-3.59-.24-.37A9.9 9.9 0 0 1 2.17 12c0-5.47 4.45-9.92 9.92-9.92 2.65 0 5.14 1.03 7.01 2.9A9.86 9.86 0 0 1 22 12c0 5.46-4.45 9.9-9.92 9.8Zm5.45-7.42c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.76.97-.93 1.17-.17.2-.34.22-.63.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.04-.17-.3-.02-.46.13-.6.13-.13.3-.34.44-.51.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.49s1.07 2.88 1.22 3.08c.15.2 2.1 3.22 5.1 4.5.71.3 1.27.49 1.71.63.72.23 1.37.2 1.88.12.57-.08 1.76-.72 2-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35Z"/>
  </svg>
);

const ListingDetail = () => {
  const { slug } = useParams();
  const location = useLocation();
  const filterParams = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const p = {};
    for (const k of ['region', 'category', 'q']) {
      const v = sp.get(k);
      if (v) p[k] = v;
    }
    return p;
  }, [location.search]);

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [neighbors, setNeighbors] = useState({ prev: null, next: null, position: null, total: null });
  const [activeMedia, setActiveMedia] = useState(0);
  const [similar, setSimilar] = useState([]);
  const { isFavorite, toggle: toggleFav } = useFavorites();

  useEffect(() => {
    if (!listing) return;
    listingsApi.list({ category: listing.category, page_size: 7 })
      .then((res) => setSimilar((res.items || []).filter((x) => x.id !== listing.id).slice(0, 4)))
      .catch(() => setSimilar([]));
  }, [listing?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLoading(true); setNotFound(false); setActiveMedia(0);
    listingsApi.detail(slug)
      .then((d) => {
        setListing(d);
        return listingsApi.neighbors(slug, filterParams);
      })
      .then(setNeighbors)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to top only when the slug changes (not on mount from browser back)
  const prevSlug = useRef(slug);
  useEffect(() => {
    if (prevSlug.current !== slug) {
      prevSlug.current = slug;
      window.scrollTo(0, 0);
    }
  }, [slug]);

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-[#9a8a83]"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  if (notFound || !listing) return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center">
      <p className="text-[#b8a9a3] mb-4">Inserat nicht gefunden.</p>
      <Link to="/inserate" className="text-[#c19a8a] hover:text-white">Zurück zu allen Inseraten</Link>
    </div>
  );

  const [g1, g2] = listing.gradient || ['#7a1f3d', '#c2185b'];
  const whatsappNumber = (listing.whatsapp || listing.phone || '').replace(/[^0-9]/g, '');
  const canCall = !!listing.phone;
  const canWhatsapp = !!whatsappNumber;
  const canMessage = !!listing.owner_id;

  // Build media list: images + video (if any)
  const imageUrls = (listing.images && listing.images.length ? listing.images : (listing.image_url ? [listing.image_url] : []));
  const mediaItems = [
    ...imageUrls.map((u) => ({ type: 'image', url: u })),
    ...(listing.video_url ? [{ type: 'video', url: listing.video_url }] : []),
  ];
  const currentMedia = mediaItems[activeMedia];

  const navSearch = Object.keys(filterParams).length
    ? '?' + new URLSearchParams(filterParams).toString() : '';

  return (
    <div className="min-h-screen bg-[#0f0a0c]">
      {/* Prev/Next bar */}
      <div className="sticky top-16 z-30 bg-[#0f0a0c]/95 backdrop-blur-md border-b border-[#2a1f24]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-2">
          <Link to={`/inserate${navSearch}`}
            className="inline-flex items-center gap-2 text-sm text-[#b8a9a3] hover:text-white">
            <Grid3x3 className="w-4 h-4" /> <span className="hidden sm:inline">Alle Inserate</span>
          </Link>
          <span className="text-xs text-[#7a6b65] hidden sm:inline">
            {neighbors.position && `${neighbors.position} von ${neighbors.total}`}
          </span>
          <div className="flex items-center gap-2">
            {neighbors.prev ? (
              <Link to={`/inserate/${neighbors.prev.slug}${navSearch}`}
                className="px-3 py-1.5 rounded-md border border-[#2a1f24] text-[#b8a9a3] hover:text-white hover:border-[#a83a5b] inline-flex items-center gap-1.5 text-sm">
                <ChevronLeft className="w-4 h-4" /> <span className="hidden sm:inline">Vorheriges</span>
              </Link>
            ) : (
              <span className="px-3 py-1.5 rounded-md border border-[#2a1f24] text-[#5a4d48] inline-flex items-center gap-1.5 text-sm opacity-50">
                <ChevronLeft className="w-4 h-4" /> <span className="hidden sm:inline">Vorheriges</span>
              </span>
            )}
            {neighbors.next ? (
              <Link to={`/inserate/${neighbors.next.slug}${navSearch}`}
                className="px-3 py-1.5 rounded-md bg-[#a83a5b] hover:bg-[#bf456b] text-white font-medium inline-flex items-center gap-1.5 text-sm">
                <span className="hidden sm:inline">Nächstes</span> <ChevronRight className="w-4 h-4" />
              </Link>
            ) : (
              <span className="px-3 py-1.5 rounded-md border border-[#2a1f24] text-[#5a4d48] inline-flex items-center gap-1.5 text-sm opacity-50">
                <span className="hidden sm:inline">Nächstes</span> <ChevronRight className="w-4 h-4" />
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Link to={`/inserate${navSearch}`} className="inline-flex items-center gap-2 text-sm text-[#b8a9a3] hover:text-white mb-4">
          <ArrowLeft className="w-4 h-4" /> Zurück zur Übersicht
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {/* Unified photo + metadata card */}
            <div className="bg-[#1a1417] border border-[#2a1f24] rounded-2xl overflow-hidden mb-6">
              <div className="relative aspect-[16/10] bg-black"
                style={{ background: !currentMedia ? `linear-gradient(135deg, ${g1} 0%, ${g2} 100%)` : undefined }}>
                {!currentMedia ? (
                  <>
                    <div className="absolute inset-0 opacity-30 mix-blend-overlay"
                      style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.4) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(0,0,0,0.5) 0%, transparent 50%)' }} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="font-serif text-9xl text-white/40">{listing.initials}</span>
                    </div>
                  </>
                ) : currentMedia.type === 'image' ? (
                  <img src={toAbs(currentMedia.url)} alt={listing.name} className="w-full h-full object-cover" />
                ) : (
                  <video src={toAbs(currentMedia.url)} controls className="w-full h-full object-contain bg-black" />
                )}
                {listing.top && (
                  <div className="absolute top-4 left-4 px-3 py-1.5 rounded bg-black/60 backdrop-blur-sm border border-[#c19a8a]/40 text-xs tracking-[0.2em] uppercase text-[#f5d4be] font-semibold inline-flex items-center gap-1.5">
                    <Star className="w-3.5 h-3.5 fill-[#f5d4be]" /> Top
                  </div>
                )}
              </div>

              {mediaItems.length > 1 && (
                <div className="grid grid-cols-6 gap-2 p-3 border-b border-[#2a1f24]">
                  {mediaItems.map((m, i) => (
                    <button key={i} onClick={() => setActiveMedia(i)}
                      className={`relative aspect-square rounded-md overflow-hidden border-2 transition-colors ${
                        activeMedia === i ? 'border-[#a83a5b]' : 'border-transparent hover:border-[#2a1f24]'
                      }`}>
                      {m.type === 'image' ? (
                        <img src={toAbs(m.url)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-black flex items-center justify-center">
                          <Play className="w-5 h-5 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}

              <div className="p-6">
                <Link to={`/inserate?category=${encodeURIComponent(listing.category)}`}
                  className="text-xs tracking-[0.2em] uppercase text-[#c19a8a] hover:text-white hover:underline inline-block mb-2">
                  {listing.category}
                </Link>
                <h1 className="font-serif text-3xl sm:text-4xl text-white mb-3">
                  {listing.name}
                  {listing.age && <span className="text-[#9a8a83] text-xl ml-2">· {listing.age}</span>}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-[#9a8a83] mb-5">
                  <Link to={`/inserate?region=${encodeURIComponent(listing.region)}`}
                    className="inline-flex items-center gap-1.5 hover:text-white hover:underline">
                    <MapPin className="w-4 h-4" />
                    {listing.region}{listing.city ? ` · ${listing.city}` : ''}
                  </Link>
                  <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" />
                    {new Date(listing.created_at).toLocaleDateString('de-CH')}
                  </span>
                </div>
                <p className="text-[#b8a9a3] leading-relaxed whitespace-pre-wrap">{listing.description}</p>
              </div>
            </div>

            {listing.service_tags && listing.service_tags.length > 0 && (
              <div className="bg-[#1a1417] border border-[#2a1f24] rounded-xl p-6 mb-6">
                <h3 className="font-serif text-xl text-white mb-4">Angebote</h3>
                <div className="flex flex-wrap gap-2">
                  {listing.service_tags.map((tag) => (
                    <span key={tag} className="px-3 py-1.5 rounded-full bg-[#241a1f] border border-[#2a1f24] text-xs text-[#c19a8a]">{tag}</span>
                  ))}
                </div>
              </div>
            )}

            {listing.languages && listing.languages.length > 0 && (
              <div className="bg-[#1a1417] border border-[#2a1f24] rounded-xl p-6">
                <h3 className="font-serif text-xl text-white mb-4 inline-flex items-center gap-2">
                  <Globe className="w-5 h-5 text-[#c19a8a]" /> Sprachen
                </h3>
                <div className="flex flex-wrap gap-2">
                  {listing.languages.map((l) => (
                    <span key={l} className="px-3 py-1.5 rounded-full bg-[#241a1f] border border-[#2a1f24] text-xs text-[#c19a8a]">{l}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="space-y-5">
            <div className="bg-[#1a1417] border border-[#2a1f24] rounded-xl p-6 lg:sticky lg:top-32">
              <p className="text-xs tracking-wider uppercase text-[#7a6b65] mb-1">Preis</p>
              <p className="font-serif text-3xl text-white mb-5">{listing.price}</p>

              {canCall ? (
                <a href={`tel:${listing.phone.replace(/\s/g, '')}`}
                  className="w-full px-4 py-3 rounded-md bg-[#a83a5b] hover:bg-[#bf456b] text-white font-medium inline-flex items-center justify-center gap-2 mb-3">
                  <Phone className="w-4 h-4" /> Anrufen
                </a>
              ) : (
                <button disabled className="w-full px-4 py-3 rounded-md bg-[#241a1f] text-[#5a4d48] font-medium inline-flex items-center justify-center gap-2 mb-3 cursor-not-allowed">
                  <Phone className="w-4 h-4" /> Keine Nummer
                </button>
              )}

              {canWhatsapp && (
                <a href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noopener noreferrer"
                  className="w-full px-4 py-3 rounded-md bg-[#128C7E] hover:bg-[#1aad9b] text-white font-medium inline-flex items-center justify-center gap-2 mb-3">
                  <WhatsappIcon className="w-4 h-4" /> WhatsApp
                </a>
              )}

              {canMessage ? (
                <button onClick={() => setMsgOpen(true)}
                  className="w-full px-4 py-3 rounded-md bg-[#241a1f] hover:bg-[#2f2229] border border-[#2a1f24] text-white font-medium inline-flex items-center justify-center gap-2">
                  <MessageCircle className="w-4 h-4" /> Nachricht senden
                </button>
              ) : (
                <button disabled className="w-full px-4 py-3 rounded-md border border-[#2a1f24] text-[#5a4d48] cursor-not-allowed inline-flex items-center justify-center gap-2">
                  <MessageCircle className="w-4 h-4" /> Nachrichten deaktiviert
                </button>
              )}

              <button onClick={() => toggleFav(listing.id)}
                className={`w-full mt-3 px-4 py-2.5 rounded-md text-sm inline-flex items-center justify-center gap-2 transition-colors ${
                  isFavorite(listing.id)
                    ? 'bg-[#241a1f] text-[#ff4d6d] hover:text-white border border-[#a83a5b]'
                    : 'text-[#c19a8a] hover:text-white'
                }`}>
                <Heart className={`w-4 h-4 ${isFavorite(listing.id) ? 'fill-[#ff4d6d]' : ''}`} />
                {isFavorite(listing.id) ? 'Favorit entfernen' : 'Zu Favoriten'}
              </button>
            </div>

            <div className="bg-[#1a1417] border border-[#2a1f24] rounded-xl p-6">
              <h4 className="font-medium text-white mb-3 inline-flex items-center gap-2"><Shield className="w-4 h-4 text-[#c19a8a]" /> Sicherheit</h4>
              <ul className="space-y-2 text-sm text-[#9a8a83]">
                <li>• Treffen zuerst an öffentlichen Orten</li>
                <li>• Keine Vorauszahlung leisten</li>
                <li>• Bei Verdacht: Kontakt blockieren</li>
                <li>• Keine persönlichen Daten teilen</li>
              </ul>
            </div>
          </aside>
        </div>

        {similar.length > 0 && (
          <div className="mt-12">
            <div className="flex items-baseline justify-between mb-5">
              <h2 className="font-serif text-2xl text-white">Ähnliche Inserate</h2>
              <Link to={`/inserate?category=${encodeURIComponent(listing.category)}`}
                className="text-sm text-[#c19a8a] hover:text-white hover:underline inline-flex items-center gap-1">
                Mehr in {listing.category} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {similar.map((l) => <ListingCard key={l.id} listing={l} />)}
            </div>
          </div>
        )}
      </div>

      <MessageModal open={msgOpen} onClose={() => setMsgOpen(false)} listing={listing} />
    </div>
  );
};

export default ListingDetail;
